import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicOffIcon from '@mui/icons-material/MicOff';
import BrushIcon from '@mui/icons-material/Brush';
import PaletteIcon from '@mui/icons-material/Palette';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as DroneMode } from '@public/images/DroneMode.svg';
import { ReactComponent as LevelEditor } from '@public/images/LevelEditor.svg';
import { HexColorPicker } from 'react-colorful';
import IconPopover from '../../../IconPopover';
import ToolBoxItem from '../../../ToolBoxItem';
import {
  BrandingListModal,
  CreateBrandingModal,
  NameBrandingModal,
  SelectBrandingModal,
  IBranding,
  mapBrandingFromApi,
} from '../../../BrandingModeModals';
import { useStyles as useBrandingModalStyles } from '../../../BrandingModeModals/styles';

import { useAdminTools } from './hooks';

import { useStyles } from './styles';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import EventsService from '@services/EventsService';
import BrandingsService from '@services/BrandingsService';
import OVERLAYS from '@constants/overlays';
import { env } from 'env';
import { IMapColors } from '@interfaces/rooms';

const { REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE } = env;

const DEFAULT_MAP_COLORS: Required<IMapColors> = {
  primaryColor: '#000000',
  secondaryColor: '#333333',
  outlineColor: '#333333',
};

const isValidHexColor = (value: string) => {
  return /^#[0-9a-f]{6}$/i.test(value.trim());
};

type ColorFieldProps = {
  label: string;
  value: string;
  onChange: (next: string) => void;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  textFieldSx?: Record<string, unknown>;
};

const ColorField: React.FC<ColorFieldProps> = ({
  label,
  value,
  onChange,
  helperText,
  error,
  disabled,
  textFieldSx,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const normalized = value?.trim() || '#000000';

  return (
    <>
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        error={error}
        helperText={helperText}
        disabled={disabled}
        fullWidth
        InputLabelProps={{
          sx: {
            color: 'rgba(255, 255, 255, 0.72)',
            '&.Mui-focused': {
              color: 'rgba(255, 255, 255, 0.88)',
            },
          },
        }}
        sx={textFieldSx}
        inputProps={{ inputMode: 'text' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disabled={disabled}
                aria-label={`${label} picker`}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: isValidHexColor(normalized)
                      ? normalized
                      : '#000000',
                    border: '1px solid rgba(255,255,255,0.18)',
                  }}
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 30, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(12px)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <HexColorPicker
            color={isValidHexColor(normalized) ? normalized : '#000000'}
            onChange={(next) => {
              onChange(next);
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

const AdminTools = () => {
  const {
    translate,
    handleCreatePOIs,
    handleDroneMode,
    handleMuteAll,
    handleLevelEditor,
  } = useAdminTools();

  const {
    currentEvent,
    currentRoom,
    currentParticipant,
    setCurrentRoom,
    setRoomSpots,
    setSelectedCustomizationUeId,
    isBrandingModeEnabled,
    setIsBrandingModeEnabled,
    currentBrandingId,
    setCurrentBrandingId,
  } = useMytaverseEvent();

  const classes = useStyles(isBrandingModeEnabled);
  const brandingModalClasses = useBrandingModalStyles();
  const { sendMessageToUnreal } = useMytaverse();

  const [showBrandingListModal, setShowBrandingListModal] = useState(false);
  const [showCreateBrandingModal, setShowCreateBrandingModal] = useState(false);
  const [showNameBrandingModal, setShowNameBrandingModal] = useState(false);
  const [showSelectBrandingModal, setShowSelectBrandingModal] = useState(false);
  const [isCloneMode, setIsCloneMode] = useState(false);
  const [selectedCloneBrandingId, setSelectedCloneBrandingId] = useState<
    string | null
  >(null);

  const [brandings, setBrandings] = useState<IBranding[]>([]);
  const [brandingsLoading, setBrandingsLoading] = useState(false);

  const mapId = currentRoom?.map?.mapId;

  const [isMapColorsOpen, setIsMapColorsOpen] = useState(false);
  const [mapColors, setMapColors] =
    useState<Required<IMapColors>>(DEFAULT_MAP_COLORS);
  const [mapColorsError, setMapColorsError] = useState<string | null>(null);
  const [isSavingMapColors, setIsSavingMapColors] = useState(false);

  const isBrandingFlowOpen =
    showBrandingListModal ||
    showCreateBrandingModal ||
    showSelectBrandingModal ||
    showNameBrandingModal;

  useEffect(() => {
    if (!isBrandingFlowOpen) {
      setBrandings([]);
      return;
    }
    if (!mapId || !currentRoom?.roomId) {
      setBrandings([]);
      setBrandingsLoading(false);
      return;
    }
    setBrandingsLoading(true);

    const loadBrandings = async () => {
      try {
        const room = await EventsService.getRoom(currentRoom.roomId);
        if (room?.activeBrandingId) {
          setCurrentRoom((prev) =>
            prev && prev.roomId === room.roomId
              ? { ...prev, activeBrandingId: room.activeBrandingId }
              : prev,
          );
        }

        const res = await BrandingsService.getBrandings({ mapId, limit: 100 });
        const activeBrandingId =
          room?.activeBrandingId ?? currentRoom?.activeBrandingId ?? null;
        const mapped = (res.brandings ?? []).map((b, i) =>
          mapBrandingFromApi(b, i, activeBrandingId),
        );
        setBrandings(mapped);

        const active = mapped.find((b) => b.isActive) || mapped[0];
        setCurrentBrandingId(active ? active.id : null);
      } catch {
        setBrandings([]);
      } finally {
        setBrandingsLoading(false);
      }
    };

    loadBrandings();
  }, [
    currentRoom?.activeBrandingId,
    currentRoom?.roomId,
    isBrandingFlowOpen,
    mapId,
    setCurrentBrandingId,
    setCurrentRoom,
  ]);

  const isLevelEditingEnabled =
    currentEvent?.world?.isLevelEditingEnabled ?? false;

  const handleOpenMapColors = () => {
    const next: Required<IMapColors> = {
      primaryColor:
        currentRoom?.mapColors?.primaryColor || DEFAULT_MAP_COLORS.primaryColor,
      secondaryColor:
        currentRoom?.mapColors?.secondaryColor ||
        DEFAULT_MAP_COLORS.secondaryColor,
      outlineColor:
        currentRoom?.mapColors?.outlineColor || DEFAULT_MAP_COLORS.outlineColor,
    };

    setMapColors(next);
    setMapColorsError(null);
    setIsMapColorsOpen(true);
  };

  const handleCloseMapColors = () => {
    if (isSavingMapColors) {
      return;
    }
    setIsMapColorsOpen(false);
  };

  const handleMapColorsModalClose = (
    _event: unknown,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handleCloseMapColors();
  };

  const handleMapColorChange = (key: keyof IMapColors) => (value: string) => {
    setMapColors((prev) => ({ ...prev, [key]: value }));
    setMapColorsError(null);
  };

  const handleSaveMapColors = async () => {
    const roomId = currentRoom?.roomId;
    if (!roomId) {
      return;
    }

    const colorsToSave: Required<IMapColors> = {
      primaryColor: mapColors.primaryColor.trim(),
      secondaryColor: mapColors.secondaryColor.trim(),
      outlineColor: mapColors.outlineColor.trim(),
    };

    if (
      !isValidHexColor(colorsToSave.primaryColor) ||
      !isValidHexColor(colorsToSave.secondaryColor) ||
      !isValidHexColor(colorsToSave.outlineColor)
    ) {
      setMapColorsError('Please use HEX colors in format #RRGGBB.');
      return;
    }

    setIsSavingMapColors(true);
    try {
      const updated = await EventsService.updateRoom(roomId, {
        mapColors: colorsToSave,
      });
      setCurrentRoom((prev) => {
        if (!prev || prev.roomId !== roomId) {
          return prev;
        }
        return {
          ...prev,
          ...(updated ? updated : {}),
          mapColors: colorsToSave,
        };
      });
      setIsMapColorsOpen(false);
    } catch {
      setMapColorsError('Failed to save map colors. Please try again.');
    } finally {
      setIsSavingMapColors(false);
    }
  };

  const handleOpenBrandingMode = () => {
    setShowBrandingListModal(true);
  };

  const handleOpenCreateBrandingModal = () => {
    setShowBrandingListModal(false);
    setShowCreateBrandingModal(true);
  };

  const handleCloseCreateBrandingModal = () => {
    setShowCreateBrandingModal(false);
  };

  const handleCloseBrandingListModal = () => {
    setShowBrandingListModal(false);
  };

  const handleToggleBranding = async (id: string) => {
    let previousBrandings: IBranding[] = [];

    setBrandings((prev) => {
      previousBrandings = prev;
      return prev.map((branding) => ({
        ...branding,
        isActive: branding.id === id,
      }));
    });
    setCurrentBrandingId(id);

    const roomId = currentRoom?.roomId;

    if (!roomId) {
      return;
    }

    try {
      await BrandingsService.activateBranding(id, roomId);

      setCurrentRoom((prev) =>
        prev && prev.roomId === roomId
          ? { ...prev, activeBrandingId: id }
          : prev,
      );
    } catch {
      setBrandings(previousBrandings);
      const active =
        previousBrandings.find((b) => b.isActive) || previousBrandings[0];
      setCurrentBrandingId(active ? active.id : null);
    }
  };

  const enterBrandingMode = async () => {
    setIsBrandingModeEnabled(true);
    if (!isCloneMode) {
      sendMessageToUnreal({
        action: 'CLEAR_SPOTS',
      });
    }

    sendMessageToUnreal({
      action: 'ENTER_TO_CUSTOMIZATION_MODE',
    });

    if (currentRoom) {
      const roomSpots = await EventsService.getRoomSpots(currentRoom.roomId);
      setRoomSpots(roomSpots);
      setSelectedCustomizationUeId(null);
    }
  };

  const handleEditBranding = async (id: string) => {
    await enterBrandingMode();
    await handleToggleBranding(id);
    setShowBrandingListModal(false);
  };

  const handleCloneFromBranding = (id: string) => {
    setSelectedCloneBrandingId(id);
    setIsCloneMode(true);
    setShowBrandingListModal(false);
    setShowNameBrandingModal(true);
  };

  const handleDeleteBranding = async (id: string) => {
    const isActiveBranding =
      currentBrandingId === id ||
      brandings.some((b) => b.id === id && b.isActive);
    if (isActiveBranding) {
      return;
    }

    let previousBrandings: IBranding[] = [];
    let deletedInState = false;
    let nextActiveBrandingId: string | null = currentBrandingId;

    setBrandings((prev) => {
      previousBrandings = prev;

      const filtered = prev.filter((b) => b.id !== id);
      if (filtered.length === 0) {
        return prev;
      }

      deletedInState = true;

      const hasActive = filtered.some((b) => b.isActive);
      if (hasActive) {
        const active = filtered.find((b) => b.isActive);
        nextActiveBrandingId = active ? active.id : null;
        return filtered;
      }

      const withActive = filtered.map((b, idx) => ({
        ...b,
        isActive: idx === 0,
      }));
      nextActiveBrandingId = withActive.length ? withActive[0].id : null;
      return withActive;
    });

    if (!deletedInState) {
      return;
    }

    setCurrentBrandingId(nextActiveBrandingId || null);

    try {
      await BrandingsService.deleteBranding(id);
    } catch {
      setBrandings(previousBrandings);
    }
  };

  const handleStartFromScratch = () => {
    setShowCreateBrandingModal(false);
    setIsCloneMode(false);
    setShowNameBrandingModal(true);
  };

  const handleCloneFromExisting = () => {
    setShowCreateBrandingModal(false);
    setShowSelectBrandingModal(true);
  };

  const handleSelectBranding = (brandingId: string) => {
    setSelectedCloneBrandingId(brandingId);
    setShowSelectBrandingModal(false);
    setIsCloneMode(true);
    setShowNameBrandingModal(true);
  };

  const handleCloseSelectBrandingModal = () => {
    setShowSelectBrandingModal(false);
  };

  const handleBackFromNameModal = () => {
    setShowNameBrandingModal(false);
    if (isCloneMode) {
      setShowSelectBrandingModal(true);
    } else {
      setShowCreateBrandingModal(true);
    }
  };

  const handleCloseNameModal = () => {
    setShowNameBrandingModal(false);
    setIsCloneMode(false);
    setSelectedCloneBrandingId(null);
  };

  const handleContinueWithName = async (name: string) => {
    setShowNameBrandingModal(false);

    const mapId = currentRoom?.map?.mapId;
    const authorEmail = currentParticipant?.email;

    if (!mapId) {
      await enterBrandingMode();
      setIsCloneMode(false);
      setSelectedCloneBrandingId(null);
      return;
    }

    try {
      const created = await BrandingsService.createBranding({
        mapId,
        name,
        ...(isCloneMode && selectedCloneBrandingId
          ? { parentBrandingId: selectedCloneBrandingId }
          : {}),
        ...(authorEmail ? { author: authorEmail } : {}),
      });
      const mapped = mapBrandingFromApi(created, 0);
      setCurrentBrandingId(mapped.id);

      const roomId = currentRoom?.roomId;
      if (roomId) {
        try {
          await BrandingsService.activateBranding(mapped.id, roomId);
          setCurrentRoom((prev) =>
            prev && prev.roomId === roomId
              ? { ...prev, activeBrandingId: mapped.id }
              : prev,
          );
        } catch {
          /* */
        }
      }

      const res = await BrandingsService.getBrandings({ mapId, limit: 100 });
      const activeBrandingId = mapped.id;
      const refreshed = (res.brandings ?? []).map((b, i) =>
        mapBrandingFromApi(b, i, activeBrandingId),
      );
      setBrandings(refreshed);
    } catch {
      /* empty */
    }

    await enterBrandingMode();
    setIsCloneMode(false);
    setSelectedCloneBrandingId(null);
  };

  const handleLeaveBrandingMode = () => {
    setShowBrandingListModal(false);
    setRoomSpots([]);
    setIsBrandingModeEnabled(false);
    setCurrentBrandingId(null);
    sendMessageToUnreal({
      action: 'LEAVE_CUSTOMIZATION_MODE',
    });
  };

  const brandingModeIcon = <BrushIcon sx={classes.brandingModeIcon} />;

  return (
    <>
      <IconPopover
        icon={<ManageAccountsIcon sx={classes.icon} />}
        label={translate('adminToolbox.toolboxLabel')}
        position={{ vertical: 'top', horizontal: 'right' }}
        analyticsName="admin"
        overlayName={OVERLAYS.ADMIN_TOOLS}
      >
        {({ onClose }) => (
          <Box component="div" sx={classes.container}>
            <Box component="div" sx={classes.header}>
              <Typography sx={classes.title}>
                {translate('adminToolbox.title', 'Admin Tools')}
              </Typography>
              <IconButton onClick={onClose} sx={classes.closeButton}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box component="div" sx={classes.tools}>
              <ToolBoxItem
                Icon={<UploadFileIcon />}
                title={translate('adminToolbox.createPOIs')}
                handleClick={handleCreatePOIs}
              />
              <ToolBoxItem
                Icon={<DroneMode />}
                title={translate('adminToolbox.droneMode')}
                handleClick={handleDroneMode}
              />
              {REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE === 'true' ? (
                <ToolBoxItem
                  Icon={<MicOffIcon />}
                  title={translate('adminToolbox.muteAll')}
                  handleClick={handleMuteAll}
                />
              ) : null}
              {isLevelEditingEnabled ? (
                <ToolBoxItem
                  Icon={<LevelEditor />}
                  title={translate('adminToolbox.levelEditor')}
                  handleClick={() => {
                    handleLevelEditor();
                    onClose();
                  }}
                />
              ) : null}
              <ToolBoxItem
                Icon={brandingModeIcon}
                title={translate('adminToolbox.brandingMode', 'Branding Mode')}
                handleClick={handleOpenBrandingMode}
              />
              <ToolBoxItem
                Icon={<PaletteIcon />}
                title={translate('adminToolbox.mapColors', 'Map Colors')}
                handleClick={() => {
                  handleOpenMapColors();
                  onClose();
                }}
              />
            </Box>
          </Box>
        )}
      </IconPopover>

      <Modal
        open={isMapColorsOpen}
        onClose={handleMapColorsModalClose}
        sx={brandingModalClasses.modalRoot}
      >
        <Box
          sx={[
            brandingModalClasses.container,
            {
              width: 620,
              maxHeight: 'unset',
              overflow: 'visible',
            },
          ]}
        >
          <Box sx={brandingModalClasses.header}>
            <Typography sx={brandingModalClasses.title}>
              {translate('adminToolbox.mapColors', 'Map Colors')}
            </Typography>
            <IconButton
              onClick={handleCloseMapColors}
              sx={brandingModalClasses.closeButton}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ padding: '6px 16px 16px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 6,
                alignItems: 'start',
              }}
            >
              <Stack spacing={1} sx={{ width: '100%' }}>
                <ColorField
                  label={translate(
                    'adminToolbox.primaryColor',
                    'Primary Color',
                  )}
                  value={mapColors.primaryColor}
                  onChange={handleMapColorChange('primaryColor')}
                  error={!!mapColorsError}
                  disabled={isSavingMapColors}
                  textFieldSx={brandingModalClasses.textField}
                  helperText={mapColorsError || undefined}
                />
              </Stack>
              <Stack spacing={1} sx={{ width: '100%' }}>
                <ColorField
                  label={translate(
                    'adminToolbox.secondaryColor',
                    'Secondary Color',
                  )}
                  value={mapColors.secondaryColor}
                  onChange={handleMapColorChange('secondaryColor')}
                  error={!!mapColorsError}
                  disabled={isSavingMapColors}
                  textFieldSx={brandingModalClasses.textField}
                />
              </Stack>
              <Stack spacing={1} sx={{ width: '100%' }}>
                <ColorField
                  label={translate(
                    'adminToolbox.outlineColor',
                    'Outline Color',
                  )}
                  value={mapColors.outlineColor}
                  onChange={handleMapColorChange('outlineColor')}
                  error={!!mapColorsError}
                  disabled={isSavingMapColors}
                  textFieldSx={brandingModalClasses.textField}
                />
              </Stack>
            </Box>

            <Box
              sx={{ ...brandingModalClasses.nameFooter, padding: '12px 0 0' }}
            >
              <Button
                variant="outlined"
                onClick={handleCloseMapColors}
                disabled={isSavingMapColors}
                sx={brandingModalClasses.cancelButton}
              >
                {translate('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveMapColors}
                disabled={isSavingMapColors}
                sx={brandingModalClasses.continueButton}
              >
                {translate('common.save', { defaultValue: 'Save' })}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <BrandingListModal
        open={showBrandingListModal}
        onClose={handleCloseBrandingListModal}
        onLeaveBrandingMode={handleLeaveBrandingMode}
        onCreateNew={handleOpenCreateBrandingModal}
        brandings={brandings}
        brandingsLoading={brandingsLoading}
        onToggleBranding={handleToggleBranding}
        onEditBranding={handleEditBranding}
        onCloneBranding={handleCloneFromBranding}
        onDeleteBranding={handleDeleteBranding}
        currentBrandingId={currentBrandingId}
      />

      <CreateBrandingModal
        open={showCreateBrandingModal}
        onClose={handleCloseCreateBrandingModal}
        onStartFromScratch={handleStartFromScratch}
        onCloneFromExisting={handleCloneFromExisting}
        hasExistingBrandings={brandings.length > 0}
      />

      <NameBrandingModal
        open={showNameBrandingModal}
        onClose={handleCloseNameModal}
        onBack={handleBackFromNameModal}
        onContinue={handleContinueWithName}
      />

      <SelectBrandingModal
        open={showSelectBrandingModal}
        onClose={handleCloseSelectBrandingModal}
        onSelect={handleSelectBranding}
        brandings={brandings}
      />
    </>
  );
};

export default AdminTools;
